<script>
import { useHead } from '@vueuse/head';
import {
  computed,
  createSSRApp,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  onServerPrefetch,
  provide,
  ref,
  useSSRContext,
  watch,
} from 'vue';
import { renderToString } from 'vue/server-renderer';
import {
  AisConfigure,
  AisHits,
  AisIndex,
  AisInstantSearchSsr,
  AisStats,
} from 'vue-instantsearch/vue3/es';
import { useRoute, useRouter } from 'vue-router';

import { CATEGORIES_INDEX, PRODUCTS_INDEX } from '@/api/algolia';
import Header5 from '@/components/base/typography/Header5.vue';
import DepartmentSuggestions from '@/components/search/departments/Suggestions.vue';
import ResultCard from '@/components/search/products/ResultCard.vue';
import ResultCardList from '@/components/search/products/ResultCardList.vue';
import HiddenMobileFiltersTrigger from '@/components/search/widgets/HiddenMobileFiltersTrigger.vue';
import NoResults from '@/components/search/widgets/NoResults.vue';
import PaginatedResultsCount from '@/components/search/widgets/PaginatedResultsCount.vue';
import Pagination from '@/components/search/widgets/Pagination.vue';
import RefinementControlPanel from '@/components/search/widgets/RefinementControlPanel.vue';
import RevealMobileFilters from '@/components/search/widgets/RevealMobileFilters.vue';
import SearchBox from '@/components/search/widgets/SearchBox.vue';
import SortBy from '@/components/search/widgets/SortBy.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useState } from '@/composables/useState';
import chevronDown from '@/icons/chevronDown';
import { ALGOLIA_USER_TOKEN_COOKIE } from '@/lib/personalization/common';
import { gtag } from '@/utils/analytics';
import { trackAlgoliaExperiment } from '@/utils/analytics/experimentViewedEvent';
import { sendProductsSearched } from '@/utils/analytics/productSearched';
import { createSearchApp } from '@/utils/createInstantSearch';
import { getCookie } from '@/utils/isomorphic/cookie';
import { adaptMultipleQueryValues, createAlgoliaRouter } from '@/utils/search/createAlgoliaRouter';
import { searchRefinementMenus as refinementMenus } from '@/utils/searchRefinementMenus';

export default defineComponent({
  name: 'Search',
  components: {
    AisConfigure,
    AisHits,
    AisIndex,
    AisInstantSearchSsr,
    AisStats,
    DepartmentSuggestions,
    Header5,
    HiddenMobileFiltersTrigger,
    NoResults,
    PaginatedResultsCount,
    Pagination,
    RefinementControlPanel,
    ResultCard,
    ResultCardList,
    RevealMobileFilters,
    SearchBox,
    SortBy,
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { flags } = useFeatureFlags();
    useHead({
      link: [{ rel: 'canonical', href: 'https://nuts.com/search' }],
      meta: [
        { name: 'robots', content: 'noindex,follow' },
        { property: 'og:title', content: 'Search Result | Nuts.com', key: 'og:title' },
        {
          content:
            'We’ve sailed the seven seas to make finding high quality and hard-to-find products easy. Shop our wide selection today.',
          key: 'og:description',
          property: 'og:description',
        },
        {
          content: 'https://nuts.com/images/email/2018/PD-1b-hero-groovy-smoothies@2x.70b69e28.png',
          key: 'og:image',
          property: 'og:image',
        },
        { property: 'og:url', content: 'http://nuts.com/search', key: 'og:url' },
      ],
      bodyAttrs: {
        class: ['listing-page'],
      },
    });

    const { query: synchronizedQuery } = useState('synchronizedSearchBar', () => ({ query: '' }));
    const queryModel = computed({
      get: () => synchronizedQuery.value,
      set: (value) => {
        synchronizedQuery.value = value;
      },
    });
    queryModel.value = route.query.query ? adaptMultipleQueryValues(route.query) : '';

    const hasDepartmentSuggestions = ref(false);

    const refinementControlPanel = ref();
    const openOverlay = (panel) => {
      refinementControlPanel.value.openOverlay(panel);
    };

    const hitsContainer = ref();
    const mobileFiltersATF = ref();

    const state = {
      CATEGORIES_INDEX,
      chevronDown,
      flags,
      hasDepartmentSuggestions,
      hitsContainer,
      hitsPerPage: 24,
      mobileFiltersATF,
      openOverlay,
      PRODUCTS_INDEX,
      queryModel,
      refinementControlPanel,
      refinementMenus,
      route,
      ruleContexts: ['Search'],
      sortByItems: [
        { value: 'Products', label: 'Popularity' },
        { value: 'ProductsByPriceDesc', label: 'Price high to low' },
        { value: 'ProductsByPriceAsc', label: 'Price low to high' },
        { value: 'ProductsByActivatedAtDesc', label: 'Newest' },
      ],
      synchronizedQuery,
      userToken: getCookie(ALGOLIA_USER_TOKEN_COOKIE, false),
    };

    const algoliaRouter = createAlgoliaRouter(router);
    const cloneComponent = (componentInstance, { mixins = [] } = {}) => {
      const options = {
        serverPrefetch: undefined,
        setup: () => {
          provide('$_ais_ssrInstantSearchInstance', state.instantsearch);
          return state;
        },
        fetch: undefined,
        _base: undefined,
        name: 'ais-ssr-root-component',
      };
      const appOptions = { ...componentInstance.$options, ...options };
      appOptions.mixins = [...mixins];
      const app = createSSRApp(appOptions, props).provide('is-ais-clone', true).use(router);

      app.$root = componentInstance.$root;

      return app;
    };
    const mixin = createSearchApp('Products', algoliaRouter, cloneComponent);
    const { instantsearch } = mixin.data();
    state.instantsearch = instantsearch;
    provide('$_ais_ssrInstantSearchInstance', instantsearch);

    const component = getCurrentInstance()?.proxy;
    const ssrContext = import.meta.env.SSR ? useSSRContext() : undefined;
    onServerPrefetch(() =>
      instantsearch
        .findResultsState({
          component,
          renderToString,
        })
        .then((algoliaState) => {
          ssrContext.algoliaState = algoliaState;
        }),
    );

    onBeforeMount(() => {
      const { __ALGOLIA_STATE__: algoliaState } = window;
      if (algoliaState) {
        instantsearch.hydrate(algoliaState);
        // eslint-disable-next-line no-underscore-dangle
        delete window.__ALGOLIA_STATE__;
      }
    });

    let previousQuery = route.query.query ?? '';

    watch(route, (r) => {
      if (r.name !== 'Search') return;

      const { query } = r.query;
      if (previousQuery !== query) {
        previousQuery = query;
        sendProductsSearched({ query, pageSection: 'Main Search Bar' });
      }
      if (query !== queryModel.value) {
        queryModel.value = query ?? '';
      }

      const algoliaResults = instantsearch.renderState?.Products.hits?.results;
      if (r.query.refinementList) {
        const filters = [];
        Object.keys(r.query.refinementList).forEach((key) => {
          r.query.refinementList[key].forEach((value) =>
            filters.push(`${key.split('.').pop()}:${value}`),
          );
        });
        gtag('event', 'apply_filter', {
          indexName: r.query.sortBy ?? 'Products',
          filters,
          queryID: algoliaResults?.queryID,
        });
      }
      trackAlgoliaExperiment(algoliaResults);

      /**
       * KNOWN HACK
       *
       * We have non-trivial CSS in the legacy nutshell bundle that depends on this boolean state.
       * Relying on the reactive route is not ideal, but it's the best we can do until `vue-instantsearch`
       * is purged from our repo in favor of direct client usage.
       */
      hasDepartmentSuggestions.value =
        instantsearch.renderState?.[CATEGORIES_INDEX]?.hits?.results.nbHits > 0;
    });

    onMounted(() => {
      window.dataLayer.push({ GDR: { pagetype: 'searchresults' } });

      const initialQuery = document.querySelector('.ais-SearchBox-input')?.value;
      if (initialQuery) queryModel.value = initialQuery;
    });

    return state;
  },
});
</script>

<template>
  <div
    class="px-4 mx-auto max-w-screen-2xl xl:px-10 search page-content"
    itemscope
    itemtype="http://schema.org/ItemList"
  >
    <h1>Search</h1>
    <AisInstantSearchSsr>
      <Teleport defer to="#categorySuggestions">
        <AisIndex :indexName="CATEGORIES_INDEX">
          <AisConfigure
            :analyticsTags="['Search']"
            distinct
            filters="idealForSuggestions:true"
            :hitsPerPage="4"
            :query="queryModel"
            :userToken
          />
          <AisHits>
            <template #default="{ items }">
              <DepartmentSuggestions
                :class="{ 'md:mt-4': !!synchronizedQuery }"
                :items
                :query="queryModel"
              />
            </template>
          </AisHits>
        </AisIndex>
      </Teleport>
      <AisIndex :indexName="PRODUCTS_INDEX">
        <AisConfigure
          :analyticsTags="['Search']"
          :clickAnalytics="true"
          filters="traits:searchable"
          :getRankingInfo="true"
          :ruleContexts
          :userToken
        />
        <div class="md:hidden">
          <div class="relative w-full section masthead-alternative">
            <div class="ptn-search-box">
              <span id="search-input-mobile">
                <SearchBox v-model="queryModel" />
              </span>
            </div>
          </div>
        </div>
        <div
          class="w-full search-wrapper"
          :class="{ 'has-department-suggestions': hasDepartmentSuggestions }"
        >
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4 xl:grid-cols-5">
            <RefinementControlPanel
              ref="refinementControlPanel"
              :refinement-menus="refinementMenus"
              :sort-by-items="sortByItems"
            >
              <template v-slot:search-bar>
                <SearchBox
                  v-model="queryModel"
                  class="hidden md:visible md:block lg:hidden"
                  id="ais-search-box-desktop"
                />
              </template>
            </RefinementControlPanel>
            <div class="col-span-3 md:px-4 xl:col-span-4">
              <div
                class="w-full department-suggestions-column"
                :class="{
                  'has-department-suggestions': hasDepartmentSuggestions || !!synchronizedQuery,
                }"
              >
                <Header5
                  v-if="!!synchronizedQuery"
                  class="hidden md:visible md:block"
                  data-test="search-results-header"
                >
                  You searched for '{{ synchronizedQuery }}'
                </Header5>
                <div id="categorySuggestions" />
              </div>
              <div class="w-full md:hidden">
                <RevealMobileFilters
                  :separateFilterSort="flags.separateFilterSortSearch"
                  :sortApplied="route.query.sortBy"
                  :sortOptions="sortByItems"
                  ref="mobileFiltersATF"
                  @open-filters="openOverlay"
                  @open-separated-filters="openOverlay('filters')"
                  @open-sort-by="openOverlay('sort')"
                />
              </div>
              <div
                class="w-full primary-column"
                :class="{
                  'has-department-suggestions': hasDepartmentSuggestions || !!synchronizedQuery,
                }"
              >
                <div class="flex">
                  <div class="w-1/4 md:w-1/2" :class="{ 'mt-4': !!synchronizedQuery }">
                    <AisStats>
                      <template v-slot:default="{ nbHits }">
                        <span class="ais-Stats-text"> {{ nbHits }} results </span>
                      </template>
                    </AisStats>
                    <AisStats>
                      <template v-slot:default="{ nbHits }">
                        <meta itemprop="numberOfItems" :content="nbHits" />
                      </template>
                    </AisStats>
                  </div>
                  <div class="w-3/4 text-right md:w-1/2">
                    <div class="hidden md:visible md:inline ptn-filter-control exclusive sort-by">
                      <p>Sort by:</p>
                      <SortBy :items="sortByItems" />
                    </div>
                  </div>
                </div>
                <div ref="hitsContainer">
                  <AisHits>
                    <template v-slot:default="{ items }">
                      <ResultCardList
                        class="grid grid-cols-2 ws-md:grid-cols-3 xl:grid-cols-4"
                        :items
                        v-slot="{ item }"
                      >
                        <meta itemprop="position" :content="item.__position" />
                        <ResultCard
                          :item
                          layout="plp"
                          page="Search"
                          :query="queryModel"
                          title="Search"
                          trackingEventLocation="search-page"
                        />
                      </ResultCardList>
                      <NoResults
                        class="flex w-full"
                        @select-search-term="(term) => (queryModel = term)"
                      />
                    </template>
                  </AisHits>
                </div>
                <div class="flex flex-wrap results-footer">
                  <div class="flex w-full md:w-1/2">
                    <Pagination :show-first="false" :show-last="false" />
                  </div>
                  <div class="flex justify-end w-full md:w-1/2">
                    <PaginatedResultsCount />
                  </div>
                </div>
                <HiddenMobileFiltersTrigger
                  v-if="!flags.separateFilterSortSearch"
                  class="fixed md:hidden"
                  :hitsContainer
                  :primaryFiltersButton="mobileFiltersATF"
                  @show-filters="openOverlay"
                />
              </div>
            </div>
          </div>
        </div>
      </AisIndex>
    </AisInstantSearchSsr>
  </div>
</template>

<style lang="scss" scoped>
.masthead-alternative {
  padding-bottom: 0;
  .ais-SearchBox {
    margin-bottom: 15px;
  }
}
</style>
